import Loader from "@/Components/Common/Loader";
import AppSidebar from "@/Components/Layout/dashboard/AppSidebar";
import { makeRequest } from "@/helpers/apiWrapper";
import { useDashboardStore } from "@/Store/DashboardStore";
import { useUserStore } from "@/Store/UserInfoStore";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Menu } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, isLoading: userLoading } = useUserStore();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { setStoreInfo } = useDashboardStore();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const showCreation = query.get("showCreation") === "true";
  const redirect =
    query.get("redirect") || "/dashboard/seller?showCreation=true";
  const { t } = useTranslation()
  useEffect(() => {
    if (!userLoading && !user?._id) {
      navigate(`/?showLogin=true&redirect=${redirect}`);
    }
  }, [userLoading, user, redirect, navigate]);
  const {
    data: store,
    isLoading,
    isError,
    isFetched,
  } = useQuery({
    queryKey: ["store", user?._id],
    queryFn: async () => {
      const res = await makeRequest({
        endpoint: "/api/store/get-user-store",
        method: "GET",
        isPublic: false,
        server: "ecommerce",
      });
      setStoreInfo(res.data);

      return res;
    },
    enabled: !!user?._id,
    retry: 0,
  });

  useEffect(() => {
    if (!isFetched || hasRedirected) return;

    if (!store && !showCreation) {
      navigate("/dashboard/buyer/manage-addresses", { replace: true });
      setHasRedirected(true);
      return;
    }

    if (store && location.pathname === "/dashboard") {
      navigate("/dashboard/seller", { replace: true });
      setHasRedirected(true);
    }
  }, [
    isFetched,
    store,
    showCreation,
    location.pathname,
    navigate,
    hasRedirected,
  ]);
  const joinSellerMutation = useMutation({
    mutationFn: async () => {
      return makeRequest({
        endpoint: `/api/account/update-account-source/${user?._id}`,
        method: "POST",
        server: "main",
        payload: { source: "web" },
        isEncrypted: true,
      });
    },
    onSuccess: () => {
      toast.success("You are now a seller!");
      setOpen(false);

      queryClient.invalidateQueries({ queryKey: ["getUser"] });

      navigate("/dashboard/seller/settings");
    },
    onError: () => {
      toast.error("Failed to join as seller");
    },
  });

  
  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Loader />
      </div>
    );
  }
  return (
    <div className="h-screen w-full flex bg-[#F8F8F8] relative">
      {!collapsed && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setCollapsed(true)}
        />
      )}

      <aside
        className={`
            fixed lg:static z-50
            h-screen
            transition-transform duration-300
            ${collapsed ? "-translate-x-full" : "translate-x-0"}
            lg:translate-x-0
          `}
      >
        <AppSidebar
          open={open}
          setOpen={setOpen}
          setCollapsed={setCollapsed}
          collapsed={collapsed}
        />
      </aside>

      <div className="flex-1 h-full overflow-y-auto">
        <div className="lg:hidden h-14 bg-white shadow flex items-center px-4">
          <Menu
            className="cursor-pointer"
            onClick={() => setCollapsed(false)}
          />
        </div>

        <main className="p-2 lg:p-4">
          <Outlet />
        </main>
      </div>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("Confirm Seller Account")}</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-600">
            {t("Are you sure you want to join as a seller? This will enable seller features on your account.")}
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="inherit">
            {t("Cancel")}
          </Button>
          <Button
            onClick={() => joinSellerMutation.mutate()}
            variant="outlined"
            disabled={joinSellerMutation.isPending}
          >
           {t("Confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
